import React from 'react';

import InformationBox from '@components/widgets/information-box/InformationBox';
import { MAIN_THEME } from '@styles';
import { valueCommaSeparated } from '@utils/formatting-utils';

import {
  AmendmentOfferDetails,
  CertificateAmendment
} from '../types/getAmendmentsDetailsResponse.types';

import DateService from 'src/core/services/DateService';
import FormattingService from 'src/core/services/FormattingService';
import {
  buildOfferDetailsBreakdownData,
  getOfferDetailsSym,
  offerDetailsBreakdownHeaders,
  offerDetailsN
} from './OfferDetailsEndorsementWidget.atom';
import { makeStyles } from 'tss-react/mui';

type Props = {
  certificateAmendment?: CertificateAmendment | null;
};

const useStyles = makeStyles()(() => ({
  container: { width: '100%' },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    alignContent: 'center',
    justifyContent: 'space-between'
  },
  title: {
    color: MAIN_THEME.color.black,
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '17px',
    display: 'block',
    textAlign: 'left'
  },
  detailContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    flexWrap: 'wrap',
    paddingTop: '10px'
  },
  mainAmountValue: {
    color: MAIN_THEME.color.black,
    fontSize: MAIN_THEME.fontSize.lg,
    lineHeight: '20px',
    float: 'left',
    fontWeight: 'bold'
  },
  premiumAmount: {
    color: MAIN_THEME.color.red,
    fontSize: MAIN_THEME.fontSize.lg,
    lineHeight: '20px',
    fontWeight: 'bold',
    float: 'right'
  }
}));

const CertificateOfferDetailsEndorsementWidgetAtom: React.FC<Props> = ({
  certificateAmendment
}) => {
  const { classes } = useStyles();

  if (!certificateAmendment) {
    return null;
  }

  const currentPQ = certificateAmendment.currentOfferDetails as
    | AmendmentOfferDetails
    | undefined;
  const variance = certificateAmendment.policyQuoteVariance as
    | AmendmentOfferDetails
    | undefined;
  const newPQ = certificateAmendment.offerDetails as
    | AmendmentOfferDetails
    | undefined;
  const policySym = certificateAmendment.policyDetails?.currency?.symbol ?? '';

  const symCur = getOfferDetailsSym(currentPQ?.currency?.symbol, policySym);
  const symVar = getOfferDetailsSym(variance?.currency?.symbol, policySym);
  const symNew = getOfferDetailsSym(newPQ?.currency?.symbol, policySym);
  const headerCurrencySymbol = getOfferDetailsSym(
    symNew,
    policySym,
    symCur,
    symVar
  );

  const headerCreatedOn = newPQ?.createdOn
    ? DateService.formatDate(newPQ.createdOn)
    : '';
  const headerStatus = FormattingService.formatEnumToDisplay(newPQ?.status ?? '');
  const headerSumInsured = `${symNew} ${valueCommaSeparated(
    offerDetailsN(newPQ?.sumInsured).toFixed(0)
  )}`;
  const headerTotalPremium = `${symNew} ${valueCommaSeparated(
    offerDetailsN(newPQ?.totalPremium).toFixed(0)
  )}`;

  const currentBreakdown = buildOfferDetailsBreakdownData(currentPQ, symCur);
  const varianceBreakdown = buildOfferDetailsBreakdownData(variance, symVar);
  const newBreakdown = buildOfferDetailsBreakdownData(newPQ, symNew);

  return (
    <InformationBox
      visibleRows={0}
      title="Certificate Offer Details"
      status={headerStatus}
      statusReason=""
      statusIcon=""
      statusColor=""
      reviewedOn=""
      subtitle={`Created On: ${headerCreatedOn}`}
      header={
        <div className={classes.container}>
          <div className={classes.titleContainer}>
            <span className={classes.title}>Coverage</span>
            <span className={classes.title}>Total Premium</span>
          </div>
          <div className={classes.detailContainer}>
            <span className={classes.mainAmountValue}>{headerSumInsured}</span>
            <span className={classes.premiumAmount}>{headerTotalPremium}</span>
          </div>
        </div>
      }
      tabs={
        [
          {
            title: 'Comparison',
            name: 'Comparison',
            data: {
              Current: currentBreakdown,
              Variance: varianceBreakdown,
              New: newBreakdown
            },
            headers: offerDetailsBreakdownHeaders(headerCurrencySymbol),
            supportsGrouping: true,
            groupingValues: ['Current', 'Variance', 'New'],
            groupingTitle: null,
            supportCycleDivision: false
          }
        ] as any[]
      }
      collapsible={false}
      initiallyCollapsed={false}
    />
  );
};

export { CertificateOfferDetailsEndorsementWidgetAtom };
